import { Theme } from '@mui/material'
import { alertClasses } from '@mui/material/Alert'
import { Components } from '@mui/material/styles/components'
import { palette } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { spacing } from 'c1ui/theme/C1Spacing'
import { C1TypographyVariants } from 'c1ui/theme/C1TypographyTheme'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react'

declare module '@mui/material/Alert' {
    export interface AlertPropsColorOverrides {
        primary: true
    }

    export interface AlertPropsVariantOverrides {
        banner: true
    }
}

export const C1AlertTheme = {
    defaultProps: {
        severity: 'info',
        variant: 'outlined',
        iconMapping: {
            success: <C1Icon icon={CircleCheck} sx={{ fontSize: 'inherit' }} />,
            info: <C1Icon icon={Info} sx={{ fontSize: 'inherit' }} />,
            primary: <C1Icon icon={Info} sx={{ fontSize: 'inherit' }} />,
            warning: <C1Icon icon={TriangleAlert} sx={{ fontSize: 'inherit' }} />,
            error: <C1Icon icon={CircleAlert} sx={{ fontSize: 'inherit' }} />,
        },
    },
    styleOverrides: {
        root: ({ theme }) => theme.unstable_sx({
            p: 3,

            [`& .${alertClasses.icon}, & .${alertClasses.message}, & .${alertClasses.action}`]: {
                py: 0,
            },

            [`& .${alertClasses.action}`]: {
                alignItems: 'center',
                flexGrow: 0,
                flexShrink: 0,
                marginRight: 0,
            },
        }),
        outlined: ({ theme, ownerState }) => theme.unstable_sx({
            color: palette[ownerState.color || 'info'].extraDark,
            borderColor: palette[ownerState.color || 'info'].light,
            backgroundColor: theme.palette.background.default,

            [`& .${alertClasses.icon} > .MuiSvgIcon-root`]: {
                color: palette[ownerState.color || 'info'].extraDark,
            },
        }),
        // This variant is actually used for SnackBar "toasts"
        banner: ({ theme, ownerState }) => theme.unstable_sx({
            backgroundColor: theme.palette.background.default,
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
            overflow: 'hidden',

            '&:after': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '8px',
                backgroundColor: theme.palette[ownerState.color || 'info'].main,
                borderTopLeftRadius: C1BorderRadius.regular,
                borderBottomLeftRadius: C1BorderRadius.regular,
            },
        }),
        filled: ({ theme, ownerState }) => theme.unstable_sx({
            backgroundColor: theme.palette[ownerState.color || 'info'].extraLight,
            color: theme.palette[ownerState.color || 'info'].extraDark,
        }),
        icon: ({ theme, ownerState }) => theme.unstable_sx({
            opacity: 1,
            height: 30,
            width: 30,
            flexShrink: 0,
            flexGrow: 0,
            display: 'flex',
            borderRadius: C1BorderRadius.regular,
            alignItems: 'center',
            justifyContent: 'center',
            color: palette[ownerState.color || 'info'].extraDark,

            ...(ownerState.variant === 'outlined' && {
                backgroundColor: theme.palette[ownerState.color || 'info'].extraLight,
            }),
        }),
        message: () => ({
            // Center text vertically with icon when there's no AlertTitle
            minHeight: 30,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }),
    },
} as Components<Theme>['MuiAlert']

export const C1AlertTitleTheme: Components<Theme>['MuiAlertTitle'] = {
    styleOverrides: {
        root: {
            ...C1TypographyVariants.h5,
            marginTop: 0,

            // Makes it so that alerts that only have a title and no description are vertically centered
            '&:only-child': {
                marginTop: spacing.xs,
            },
        },
    },
}
